import { sliderAnatomy as parts } from '@chakra-ui/anatomy'
import {
  createMultiStyleConfigHelpers,
  defineStyle,
} from '@chakra-ui/styled-system'
import { orient } from '@chakra-ui/theme-tools'

import { space } from './tokens'

const { defineMultiStyleConfig, definePartsStyle } =
  createMultiStyleConfigHelpers(parts.keys)

const $thumbSize = space.md
const $trackSize = space.xs

const baseStyleContainer = defineStyle((props) => {
  const { orientation } = props

  return {
    display: 'inline-block',
    position: 'relative',
    cursor: 'pointer',
    _disabled: {
      opacity: 0.6,
      cursor: 'default',
      pointerEvents: 'none',
    },
    ...orient({
      orientation,
      vertical: { h: '100%' },
      horizontal: { w: '100%' },
    }),
  }
})

const baseStyleTrack = defineStyle((props) => {
  const orientationStyles = orient({
    orientation: props.orientation,
    horizontal: { h: $trackSize },
    vertical: { w: $trackSize },
  })

  return {
    ...orientationStyles,
    bg: 'softestOrange',
    overflow: 'hidden',
    borderRadius: '0.25rem',
    zIndex: 1,
  }
})

const baseStyleThumb = defineStyle((props) => {
  const { orientation } = props
  const orientationStyle = orient({
    orientation,
    vertical: {
      left: '50%',
      transform: `translateX(-50%)`,
      _active: {
        transform: `translateX(-50%) scale(1.15)`,
      },
    },
    horizontal: {
      top: '50%',
      transform: `translateY(-50%)`,
      _active: {
        transform: `translateY(-50%) scale(1.15)`,
      },
    },
  })

  return {
    ...orientationStyle,
    w: $thumbSize,
    h: $thumbSize,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    outline: 0,
    zIndex: 1,
    borderRadius: 'full',
    bg: 'orange',
    boxShadow: 'base',
    border: '1px solid',
    borderColor: 'transparent',
    transitionProperty: 'transform',
    transitionDuration: 'normal',
    _focusVisible: {
      boxShadow: 'outline',
    },
    _disabled: {
      bg: 'softestOrange',
    },
  }
})

const baseStyleFilledTrack = defineStyle(() => {
  return {
    width: 'inherit',
    height: 'inherit',
    bg: 'orange',
  }
})

const baseStyleMark = defineStyle(() => {
  return {
    height: 'inherit',
    color: 'softBlack',
    transform: 'translate(-50%,-50%)',
    width: 'fit-content',
  }
})

const baseStyle = definePartsStyle((props) => ({
  container: baseStyleContainer(props),
  track: baseStyleTrack(props),
  thumb: baseStyleThumb(props),
  filledTrack: baseStyleFilledTrack(),
  mark: baseStyleMark(),
}))

const sizeLg = definePartsStyle({
  container: {
    [$thumbSize]: `sizes.4`,
    [$trackSize]: `sizes.1`,
  },
})

const sizeMd = definePartsStyle({
  container: {
    [$thumbSize]: `sizes.3.5`,
    [$trackSize]: `sizes.1`,
  },
})

const sizeSm = definePartsStyle({
  container: {
    [$thumbSize]: `sizes.2.5`,
    [$trackSize]: `sizes.0.5`,
  },
})

const sizes = {
  lg: sizeLg,
  md: sizeMd,
  sm: sizeSm,
}

export const sliderTheme = defineMultiStyleConfig({
  baseStyle,
  sizes,
  defaultProps: {
    size: 'md',
    colorScheme: 'blue',
  },
})
